import { useMemo, useState } from 'react';
import type { WebhookEventRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useMarkWebhookProcessed,
  useWebhookEvents,
  type WebhookListFilters,
} from '../api/webhookEvents.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';

/**
 * `/webhook-events` — observability for the generic webhook inbox.
 * Epic #109 P3.16.
 *
 * Filters by provider, processed flag, and a since-timestamp. Shows
 * newest-first table with a `Mark processed` action on pending rows.
 * `payload` is collapsed into a truncated monospaced block; operators
 * expand via native `<details>` without leaving the page.
 */

export function WebhookEventsPage(): JSX.Element {
  const t = useT();
  const [provider, setProvider] = useState('');
  const [processed, setProcessed] = useState<'all' | 'true' | 'false'>('all');
  const [since, setSince] = useState('');

  const filters = useMemo<WebhookListFilters>(() => {
    return {
      ...(provider.length > 0 ? { provider } : {}),
      ...(processed !== 'all' ? { processed } : {}),
      ...(since.length > 0 ? { since } : {}),
    };
  }, [provider, processed, since]);

  const query = useWebhookEvents(filters);

  return (
    <section className="flex flex-col gap-8" data-testid="webhook-events-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('webhook_events.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('webhook_events.subtitle')}</p>
      </header>

      <article
        data-testid="webhook-events-filters-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.08em] text-outline">
            {t('webhook_events.filter.provider')}
            <input
              type="text"
              value={provider}
              onChange={(e) => setProvider(e.currentTarget.value)}
              data-testid="webhook-events-filter-provider"
              placeholder={t('webhook_events.filter.provider_placeholder')}
              className="min-w-[180px] rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.08em] text-outline">
            {t('webhook_events.filter.processed')}
            <select
              value={processed}
              onChange={(e) => setProcessed(e.currentTarget.value as 'all' | 'true' | 'false')}
              data-testid="webhook-events-filter-processed"
              className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
            >
              <option value="all">{t('webhook_events.filter.processed.all')}</option>
              <option value="false">{t('webhook_events.filter.processed.pending')}</option>
              <option value="true">{t('webhook_events.filter.processed.done')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.08em] text-outline">
            {t('webhook_events.filter.since')}
            <input
              type="text"
              value={since}
              onChange={(e) => setSince(e.currentTarget.value)}
              data-testid="webhook-events-filter-since"
              placeholder="2026-04-01T00:00:00Z"
              className="min-w-[220px] rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
            />
          </label>
        </div>
      </article>

      <article
        data-testid="webhook-events-list-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        {query.isLoading ? (
          <p className="text-xs text-outline" data-testid="webhook-events-loading">
            {t('webhook_events.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="text-xs text-error"
            role="alert"
            data-testid="webhook-events-error"
          >
            {t('webhook_events.error')}
          </p>
        ) : (query.data ?? []).length === 0 ? (
          <p className="text-xs text-outline" data-testid="webhook-events-empty">
            {t('webhook_events.empty')}
          </p>
        ) : (
          <EventsTable events={query.data ?? []} />
        )}
      </article>
    </section>
  );
}

interface EventsTableProps {
  readonly events: readonly WebhookEventRecord[];
}

function EventsTable({ events }: EventsTableProps): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table
        className="w-full text-left text-xs"
        data-testid="webhook-events-table"
      >
        <thead>
          <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('webhook_events.column.created_at')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('webhook_events.column.provider')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('webhook_events.column.event_type')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('webhook_events.column.status')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('webhook_events.column.payload')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('webhook_events.column.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => (
            <EventRow key={ev.id} event={ev} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface EventRowProps {
  readonly event: WebhookEventRecord;
}

function EventRow({ event }: EventRowProps): JSX.Element {
  const t = useT();
  const mark = useMarkWebhookProcessed();
  const toast = useToast();
  const isProcessed = event.processed_at !== null;

  async function handleMark(): Promise<void> {
    try {
      await mark.mutateAsync(event.id);
      toast.success(t('webhook_events.toast.marked'));
    } catch (err) {
      const msg =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(msg);
    }
  }

  return (
    <tr
      className="border-b border-outline-variant/40"
      data-testid={`webhook-events-row-${event.id}`}
      data-processed={isProcessed ? 'true' : 'false'}
    >
      <td className="py-2 pr-4 font-mono text-[11px] text-outline">{event.created_at}</td>
      <td className="py-2 pr-4 text-on-surface">{event.provider}</td>
      <td className="py-2 pr-4 font-mono text-[11px] text-on-surface">{event.event_type}</td>
      <td className="py-2 pr-4">
        <span
          className={
            isProcessed
              ? 'inline-flex items-center rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase text-on-surface'
              : 'inline-flex items-center rounded-full bg-tertiary-container px-2 py-0.5 text-[10px] font-bold uppercase text-on-tertiary-container'
          }
        >
          {isProcessed
            ? t('webhook_events.status.processed')
            : t('webhook_events.status.pending')}
        </span>
      </td>
      <td className="py-2 pr-4">
        <details>
          <summary className="cursor-pointer text-[11px] text-outline">
            {t('webhook_events.payload.expand')}
          </summary>
          <pre
            className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap rounded bg-surface-container-lowest p-2 font-mono text-[10px] text-on-surface"
            data-testid={`webhook-events-payload-${event.id}`}
          >
            {event.payload}
          </pre>
        </details>
      </td>
      <td className="py-2 pr-4 text-right">
        {isProcessed ? (
          <span className="text-[11px] text-outline">—</span>
        ) : (
          <button
            type="button"
            onClick={() => void handleMark()}
            disabled={mark.isPending}
            data-testid={`webhook-events-mark-${event.id}`}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
          >
            {mark.isPending
              ? t('webhook_events.action.marking')
              : t('webhook_events.action.mark_processed')}
          </button>
        )}
      </td>
    </tr>
  );
}
